import React from "react";
import { motion } from "framer-motion";
import {
  Layers,
  Cloud,
  Cpu,
  Shield,
  Globe,
  Code,
  Database,
  Settings,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function ITSolutions() {
  return (
    <div className="w-full text-gray-800 font-sans">

      {/* ================= HERO ================= */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 text-white py-28 px-6"
      >
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8">
              Enterprise IT Solutions & Software Engineering
            </h1>

            <p className="text-lg md:text-xl text-blue-100 mb-10 max-w-xl">
              We design, build, modernize, and operate enterprise-grade software
              systems that support complex business processes, high scale, and
              long-term technology evolution.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="px-6 py-3 rounded-lg font-semibold
                           bg-white/20 backdrop-blur-md border border-white/30
                           hover:bg-white/30 transition"
              >
                Discuss IT Requirements
              </Link>
            </div>
          </div>

          <div className="hidden lg:block space-y-5">
            {[
              "Business-aligned enterprise architecture",
              "Cloud-native and data-driven systems",
              "Secure, scalable, production-ready platforms",
              "Long-term ownership and system evolution",
            ].map((item) => (
              <div key={item} className="flex gap-4 items-start">
                <span className="text-blue-300 text-xl">›</span>
                <p className="text-lg font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ================= HOW WE APPROACH IT SOLUTIONS ================= */}
      <section className="py-28 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-10">
            How We Approach Enterprise IT Solutions
          </h2>

          <div className="grid lg:grid-cols-2 gap-16">
            <p className="text-gray-600 leading-relaxed text-lg">
              TechX Incorporation approaches IT solution delivery as a strategic
              engineering initiative. We begin by understanding business
              workflows, operational constraints, regulatory considerations,
              and the existing technology landscape before proposing any
              technical direction.
            </p>

            <p className="text-gray-600 leading-relaxed text-lg">
              Our focus is on building systems that are not only functional at
              launch, but resilient, maintainable, and adaptable as business
              needs and technology ecosystems evolve over time.
            </p>
          </div>
        </div>
      </section>

      {/* ================= CORE SOLUTION AREAS ================= */}
      <section className="py-28 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-20">
            Core Enterprise Solution Areas
          </h2>

          <div className="grid md:grid-cols-3 gap-10">
            <SolutionCard
              icon={Layers}
              title="Enterprise Application Engineering"
              desc="Design and development of mission-critical enterprise applications, including internal platforms, workflow systems, customer-facing portals, and integration-heavy applications. Emphasis is placed on clean architecture, scalability, fault tolerance, and long-term maintainability."
            />
            <SolutionCard
              icon={Cloud}
              title="Cloud-Native & Data-Centric Platforms"
              desc="Architecture and implementation of cloud-native systems, data engineering pipelines, analytics platforms, and distributed services. Solutions are designed for performance, security, cost efficiency, and operational reliability in multi-cloud or hybrid environments."
            />
            <SolutionCard
              icon={Cpu}
              title="AI-Enabled & Intelligent Systems"
              desc="Development and integration of AI-driven capabilities such as intelligent automation, predictive analytics, decision-support systems, and advanced data-driven applications embedded into enterprise workflows."
            />
          </div>
        </div>
      </section>

      {/* ================= TECHNOLOGY & PLATFORM EXPERTISE ================= */}
      <section className="py-28 px-6 bg-gray-900 text-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-10">
            Technology & Platform Expertise
          </h2>

          <p className="text-gray-300 max-w-4xl mb-20 leading-relaxed">
            Our technology choices are driven by architectural suitability,
            enterprise standards, and long-term supportability rather than
            short-term trends or vendor bias.
          </p>

          <div className="grid md:grid-cols-4 gap-10">
            <TechBlock
              icon={Code}
              title="Application & API Development"
            />
            <TechBlock
              icon={Database}
              title="Data Platforms & Databases"
            />
            <TechBlock
              icon={Cloud}
              title="Cloud Infrastructure & Platforms"
            />
            <TechBlock
              icon={Shield}
              title="Security, Governance & Compliance"
            />
          </div>
        </div>
      </section>

      {/* ================= DELIVERY & ENGAGEMENT MODEL ================= */}
      <section className="py-28 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-20">
            Delivery & Engagement Model
          </h2>

          <div className="grid md:grid-cols-3 gap-10">
            <DeliveryCard
              icon={Settings}
              title="Architecture & Solution Design"
              desc="Upfront system design, technology selection, and architectural validation aligned with enterprise standards, security requirements, and scalability goals."
            />
            <DeliveryCard
              icon={Code}
              title="Engineering, Integration & Testing"
              desc="Incremental development, system integration, automated testing, and quality assurance aligned with real production workflows."
            />
            <DeliveryCard
              icon={Globe}
              title="Global Delivery & Long-Term Support"
              desc="Flexible engagement models supporting distributed teams, multi-region deployments, ongoing enhancements, and system evolution."
            />
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="py-28 px-6 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Design and Build Enterprise-Ready Technology Systems
          </h2>

          <p className="text-blue-100 mb-10 leading-relaxed">
            Speak with our consultants to discuss your IT challenges, system
            modernization goals, and long-term technology strategy.
          </p>

          <Link
            to="/contact"
            className="inline-block px-8 py-4 rounded-lg font-semibold
                       bg-white/20 backdrop-blur-md border border-white/30
                       hover:bg-white/30 transition"
          >
            Request Consultation
          </Link>
        </div>
      </section>

    </div>
  );
}

/* ================= COMPONENTS ================= */

function SolutionCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition">
      <Icon className="text-blue-600 mb-4" size={28} />
      <h3 className="font-semibold text-xl mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function TechBlock({ icon: Icon, title }) {
  return (
    <div className="bg-gray-800 rounded-2xl p-8 text-center hover:bg-gray-700 transition">
      <Icon className="text-blue-400 mx-auto mb-4" size={28} />
      <h4 className="font-semibold text-lg text-white">{title}</h4>
    </div>
  );
}

function DeliveryCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-8 shadow-sm hover:shadow-md transition">
      <Icon className="text-blue-600 mb-4" size={28} />
      <h3 className="font-semibold text-xl mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{desc}</p>
    </div>
  );
}
